import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import * as Linking from 'expo-linking';
import { useFocusEffect, useRouter, type Href } from 'expo-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { REFERRAL_BASE_URL } from '@/constants/links';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import useClipboard from '@/hooks/useClipboard';
import { useToaster } from '@/hooks/useToaster';
import { getUserAttributes } from '@/services/auth';
import {
  getAnnouncementsV2QueryOptions,
  type AnnouncementConfig,
} from '@/services/getAnnouncementsV2';
import {
  subscribeToAnnouncementRotation,
  useAnnouncementRotationStore,
} from '@/services/stores/announcementRotationStore';
import { useAnnouncementSlotStore } from '@/services/stores/announcementSlotStore';
import {
  trackAnnouncementClicked,
  trackAnnouncementInviteLinkCopied,
  trackAnnouncementLinkNavigated,
  type AnnouncementAnalyticsContext,
} from '@/util/analyticsEvents';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { getPlatformStorage } from '@/util/platformStorage';
import { Sentry } from '@/util/sentry';
import { extractAppPath } from '@/utils/linkHelpers';

/**
 * Opens an external URL with appropriate behavior for the platform.
 * - On web: Opens in a new tab/window
 * - On native: Uses the device's default browser
 */
export function openExternalLink(url: string): void {
  if (isWeb) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    Linking.openURL(url);
  }
}

/**
 * The announcement's identity, derived from what it says.
 *
 * The payload carries no id and the query refetches on an interval, so object
 * identity says nothing about whether this is the same message the reader saw a
 * minute ago. Content is the only thing that distinguishes one announcement from
 * the next, which makes it the only honest key for telling a rotation from a re-render.
 */
export function buildAnnouncementSignature(config: AnnouncementConfig): string {
  return [
    config.type,
    config.variant,
    config.title,
    config.message,
    config.emoji,
    config.showIcon,
    config.ctaLabel,
    config.ctaUrl,
    config.ctaAction,
  ]
    .map((part) => (part === undefined || part === null ? '' : String(part)))
    .join('\u0000');
}

/**
 * Hook for announcement banner data and CTA handling
 * Responsibilities:
 * - Navigate internally for app routes, open external URLs in new tab (web)
 * - Track CTA clicks with username, userId, ctaUrl, and action type
 * - Support custom actions: copy_referral_code, navigate
 * - On web, send an account that may not take the CTA's action to the step that lets it:
 *   copying a referral code needs `create` on `Referral`; a navigation needs only that
 *   the account is not parked on NPI verification, since the destination gates itself
 */
export function useAnnouncements() {
  const { userId, currentUser, needsNpiVerification } = useAuthStatus();

  const { guard, requestAccess } = useAbilityGate();

  const [localBannerEnabled, setLocalBannerEnabled] = useState<boolean | null>(null);

  const { copyToClipboard } = useClipboard();

  const toast = useToaster();

  const { data: announcements } = useQuery({
    ...getAnnouncementsV2QueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const router = useRouter();

  const tick = useAnnouncementRotationStore((state) => state.tick);

  /**
   * The announcements on offer, in the server's priority order.
   *
   * `enabled` is re-checked rather than assumed: the server only sends live
   * announcements, but the field is in the payload and a client that ignores it
   * would show one the moment that stops being true.
   */
  const eligible = useMemo(
    () => (announcements ?? []).filter((announcement) => announcement.enabled ?? false),
    [announcements]
  );

  /**
   * The one on screen, chosen by the shared clock rather than by this caller, so
   * every mounted banner shows the same announcement.
   */
  const config = eligible.length > 0 ? eligible[tick % eligible.length] : undefined;

  const signature = config ? buildAnnouncementSignature(config) : null;

  const shouldRotate = eligible.length > 1;

  useEffect(() => {
    if (!shouldRotate) {
      return;
    }

    return subscribeToAnnouncementRotation();
  }, [shouldRotate]);

  /**
   * The intent event: exactly one per CTA press, whatever the press turns out to
   * do. Each navigation branch additionally reports where it went through
   * `ANNOUNCEMENT_LINK_NAVIGATED`, so the two answer different questions and the
   * external branch no longer reports itself as a second click.
   *
   * Known divergence: the leading debounce guards this event only, so a
   * double-tap yields one click and two navigations. Changing that is a
   * warehouse decision, not a cleanup.
   */
  const trackClickedOnce = useDebouncedCallback(
    (context: AnnouncementAnalyticsContext) => {
      trackAnnouncementClicked(context);
    },
    1000,
    { leading: true, trailing: false }
  );

  const loadBannerSetting = useCallback(async () => {
    try {
      const storage = getPlatformStorage(undefined, 'local');

      const storedValue = await storage.getItem(STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED);

      setLocalBannerEnabled(storedValue === null ? true : storedValue === 'true');
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'AnnouncementBanner',
          source: 'banner-setting-load',
        },
        extra: {
          errorMessage: error instanceof Error ? error.message : String(error),
          storageKey: STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED,
        },
      });

      setLocalBannerEnabled(true);
    }
  }, []);

  useEffect(() => {
    loadBannerSetting();
  }, [loadBannerSetting]);

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED) {
        loadBannerSetting();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadBannerSetting]);

  const handleCtaPress = async () => {
    let username: string | undefined;

    try {
      const userAttributes = await getUserAttributes();

      const firstName = userAttributes['custom:first_name'];

      const lastName = userAttributes['custom:last_name'];

      username = firstName && lastName ? `${firstName} ${lastName}` : undefined;
    } catch {
      username = undefined;
    }

    trackClickedOnce({
      username,
      userId,
      ctaUrl: config?.ctaUrl,
      announcementType: config?.type,
      announcementTitle: config?.title,
      ctaAction: config?.ctaAction,
    });

    if (isWeb && currentUser) {
      if (config?.ctaAction === 'copy_referral_code') {
        if (!guard('create', 'Referral')) {
          return;
        }
      } else if (needsNpiVerification) {
        requestAccess();

        return;
      }
    }

    if (config?.ctaAction === 'copy_referral_code') {
      if (!currentUser?.referralCode) {
        toast.showError({
          title: 'No referral code available',
          message:
            "You don't have a referral code yet. Please contact support if you need assistance.",
        });

        trackAnnouncementInviteLinkCopied({
          username,
          userId,
          ctaUrl: config?.ctaUrl,
          announcementType: config?.type,
          announcementTitle: config?.title,
          ctaAction: config?.ctaAction,
          hasReferralCode: false,
        });

        return;
      }

      const referralLink = `${REFERRAL_BASE_URL}${currentUser.referralCode}`;

      copyToClipboard(referralLink, () => {
        trackAnnouncementInviteLinkCopied({
          username,
          userId,
          ctaUrl: config?.ctaUrl,
          announcementType: config?.type,
          announcementTitle: config?.title,
          ctaAction: config?.ctaAction,
          hasReferralCode: true,
        });
      });

      return;
    }

    if (config?.ctaAction === 'navigate' || !config?.ctaAction) {
      if (!config?.ctaUrl) {
        return;
      }

      const appPath = extractAppPath(config.ctaUrl);

      if (appPath && !isWeb) {
        trackAnnouncementLinkNavigated({
          username,
          userId,
          ctaUrl: config.ctaUrl,
          announcementType: config.type,
          announcementTitle: config.title,
          ctaAction: config.ctaAction,
          navigationType: 'deep-link',
        });

        try {
          router.push(appPath as Href);
        } catch (error) {
          Sentry.captureException(error, {
            extra: { appPath, ctaUrl: config.ctaUrl },
          });

          openExternalLink(config.ctaUrl);
        }

        return;
      }

      if (config.ctaUrl.startsWith('/')) {
        trackAnnouncementLinkNavigated({
          username,
          userId,
          ctaUrl: config.ctaUrl,
          announcementType: config.type,
          announcementTitle: config.title,
          ctaAction: config.ctaAction,
          navigationType: 'relative-path',
        });

        try {
          router.push(config.ctaUrl as Href);
        } catch (error) {
          Sentry.captureException(error, {
            extra: { ctaUrl: config.ctaUrl },
          });
        }

        return;
      }

      trackAnnouncementLinkNavigated({
        username,
        userId,
        ctaUrl: config.ctaUrl,
        announcementType: config.type,
        announcementTitle: config.title,
        ctaAction: config.ctaAction,
        navigationType: 'external',
      });

      openExternalLink(config.ctaUrl);
    }
  };

  return {
    config,
    handleCtaPress,
    /**
     * Whether an announcement is eligible to show at all. The slot and the
     * native header both gate on this and nothing else.
     *
     * There used to be a second, promo-subtracted value here for the native
     * path to keep out of the promo's way. It never subtracted anything:
     * `useFellowMentorBanner` gates `isShowing` on `isWeb`, so it is always
     * false on native, and on web the slot renders both banners itself and
     * needs eligibility unfiltered. Two names, one value.
     *
     * Reads off `config` rather than repeating the filter: `config` is the pick
     * from the eligible list, so it exists exactly when something is showable.
     */
    isAnnouncementReady: config !== undefined && localBannerEnabled === true,
    /** Identifies the announcement on screen right now, for a caller that has to outlive it. */
    announcementSignature: signature,
    /** How many announcements are in the rotation, the one on screen included. */
    eligibleAnnouncementCount: eligible.length,
  };
}

/**
 * Declares that this screen is a place an announcement belongs, and returns the
 * same announcement data as `useAnnouncements`.
 *
 * Held apart from `useAnnouncements` because the slot that renders the banner
 * calls that hook too, and it is mounted in the secure layout on every screen.
 * With registration inside the shared hook the slot registered itself, so every
 * secure screen counted as an announcement surface and the scoping meant
 * nothing. Hosting and rendering are different jobs, so they are different
 * hooks.
 *
 * Scoped to focus rather than mount, because a tab navigator keeps background
 * screens mounted and their announcements sit behind nothing the reader can see.
 */
export function useAnnouncementSurface() {
  const announcements = useAnnouncements();

  const addWaiting = useAnnouncementSlotStore((state) => state.addWaiting);

  const removeWaiting = useAnnouncementSlotStore((state) => state.removeWaiting);

  const { isAnnouncementReady } = announcements;

  useFocusEffect(
    useCallback(() => {
      if (!isAnnouncementReady) {
        return;
      }

      addWaiting();

      return removeWaiting;
    }, [isAnnouncementReady, addWaiting, removeWaiting])
  );

  return announcements;
}
